import { useEffect } from "react";
import { useLocation } from "react-router-dom";

type SeoProps = {
    title: string
    description: string
    image?: string
}

function upsertMetaByName(name: string, content: string) {
    let meta = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
    }
    meta.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
    let meta = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
    if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
    }
    meta.setAttribute("content", content);
}

const Seo: React.FC<SeoProps> = ({ title, description, image = "/img/banner-7.png" }) => {
    const location = useLocation();

    useEffect(() => {
        const prevTitle = document.title;
        const prevDescriptionMeta = document.head.querySelector(`meta[name="description"]`) as HTMLMetaElement | null;
        const prevDescription = prevDescriptionMeta?.getAttribute("content") ?? null;

        document.title = title;

        const origin = window.location.origin;
        const url = `${origin}${location.pathname}${location.search}`;
        const imageUrl = image.startsWith("http") ? image : `${origin}${image}`;

        upsertMetaByName("description", description);
        upsertMetaByName("robots", "index,follow");

        upsertMetaByProperty("og:title", title);
        upsertMetaByProperty("og:description", description);
        upsertMetaByProperty("og:type", "website");
        upsertMetaByProperty("og:url", url);
        upsertMetaByProperty("og:image", imageUrl);

        upsertMetaByName("twitter:card", "summary_large_image");
        upsertMetaByName("twitter:title", title);
        upsertMetaByName("twitter:description", description);
        upsertMetaByName("twitter:image", imageUrl);

        let canonical = document.head.querySelector(`link[rel="canonical"]`) as HTMLLinkElement | null;
        const prevCanonical = canonical ? canonical.getAttribute("href") : null;
        const hadCanonical = canonical !== null;
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", url);
        return () => {
            document.title = prevTitle;

            // Restore description if it existed before.
            if (prevDescriptionMeta) {
                if (prevDescription !== null) {
                    prevDescriptionMeta.setAttribute("content", prevDescription);
                } else {
                    prevDescriptionMeta.removeAttribute("content");
                }
            }

            // Restore canonical if it existed before.
            if (hadCanonical && canonical) {
                if (prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
            }
            // If canonical didn't exist before, remove the one created by this component.
            if (!hadCanonical && canonical && canonical.parentElement) {
                canonical.parentElement.removeChild(canonical);
            }
        };
    }, [title, description, image, location.pathname, location.search]);

    return null;
};

export default Seo;

